import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';

export interface ProfileBottomBarProps {
  completionPercent: number;
  onSetupPress: () => void;
  onAddPress: () => void;
  onPreviewPress: () => void;
  onStylePress: () => void;
}

interface BarItemProps {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}

function BarItem({ icon, label, onClick }: BarItemProps) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        minWidth: 56,
        height: 52,
        padding: '0 10px',
        borderRadius: 14,
        background: hover ? 'rgba(0,0,0,0.06)' : 'transparent',
        transition: 'background-color 140ms',
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-primary)',
          width: 22,
          height: 22,
        }}
      >
        {icon}
      </span>
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 500,
          letterSpacing: 0.1,
          color: 'var(--text-secondary)',
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </button>
  );
}

/**
 * Neutral progress dial: no purple. Uses text-primary on a soft track so it
 * sits inside the Tabbio palette and reads against both light and dark menus.
 */
function SetupDial({ completionPercent }: { completionPercent: number }) {
  const safe = Math.max(0, Math.min(100, Math.round(completionPercent)));
  const angle = Math.round((safe / 100) * 360);
  return (
    <div
      style={{
        position: 'relative',
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: `conic-gradient(var(--text-primary) ${angle}deg, rgba(0,0,0,0.10) 0deg)`,
        padding: 2,
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: 'var(--menu-bg)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <span
          style={{
            fontSize: 8.5,
            fontWeight: 700,
            color: 'var(--text-primary)',
            lineHeight: 1,
            letterSpacing: 0,
          }}
        >
          {safe}
        </span>
      </div>
    </div>
  );
}

export function ProfileBottomBar({
  completionPercent,
  onSetupPress,
  onAddPress,
  onPreviewPress,
  onStylePress,
}: ProfileBottomBarProps) {
  const { t } = useTranslation('common');

  return (
    <>
      {/*
        Outer rail spans the content panel, which is the nearest positioned
        ancestor (`.tabbio-content-panel` is `position: relative`). It used to
        be viewport-fixed and offset by `--tabbio-sidebar-width`, but in the
        three-pane shell the content no longer starts at the sidebar edge —
        the agent chat column sits between them — so anchoring to the panel is
        both simpler and correct at every breakpoint.
      */}
      <div
        style={{
          position: 'absolute',
          bottom: 22,
          insetInlineStart: 0,
          insetInlineEnd: 0,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
          zIndex: 60,
          paddingInline: 16,
        }}
      >
        <div
          role="toolbar"
          aria-label="Profile builder"
          style={{
            pointerEvents: 'auto',
            display: 'inline-flex',
            alignItems: 'stretch',
            gap: 2,
            padding: 4,
            borderRadius: 9999,
            background: 'var(--menu-bg)',
            border: '1px solid var(--input-border)',
            boxShadow:
              '0 2px 6px rgba(0,0,0,0.04), 0 12px 32px rgba(0,0,0,0.10)',
            backdropFilter: 'saturate(180%) blur(16px)',
            WebkitBackdropFilter: 'saturate(180%) blur(16px)',
            fontFamily:
              "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          }}
        >
          <BarItem
            icon={<SetupDial completionPercent={completionPercent} />}
            label={t('publicProfile.setup', { defaultValue: 'Setup' })}
            onClick={onSetupPress}
          />
          <BarDivider />
          <BarItem
            icon={<MaterialSymbol name="add" size={20} />}
            label={t('publicProfile.add', { defaultValue: 'Add' })}
            onClick={onAddPress}
          />
          <BarItem
            icon={<MaterialSymbol name="visibility" size={20} />}
            label={t('publicProfile.preview', { defaultValue: 'Preview' })}
            onClick={onPreviewPress}
          />
          <BarItem
            icon={<MaterialSymbol name="palette" size={20} />}
            label={t('publicProfile.style', { defaultValue: 'Style' })}
            onClick={onStylePress}
          />
        </div>
      </div>
    </>
  );
}

function BarDivider() {
  return (
    <span
      aria-hidden
      style={{
        alignSelf: 'center',
        width: 1,
        height: 24,
        background: 'rgba(0,0,0,0.06)',
        margin: '0 2px',
        borderRadius: 1,
      }}
    />
  );
}
